import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDoctorContext } from "../../../Navigation/context/DoctorContext";
import { fetchNotificationHistory, respondToRequest } from "../https/notifications_module.https";

interface Notif {
  id: string;
  title: string;
  desc: string;
  time: string;
  type: "completed" | "cancelled" | "request" | "prescription" | "system";
  icon: string;
  unread: boolean;
  day: "today" | "yesterday" | "older";
}

const isToday = (date: Date) => date.toDateString() === new Date().toDateString();
const isYesterday = (date: Date) => {
  const y = new Date(); y.setDate(y.getDate() - 1);
  return date.toDateString() === y.toDateString();
};

const relativeTime = (dateStr: string): string => {
  const diffMins = Math.floor((Date.now() - new Date(dateStr).getTime()) / 60000);
  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins} min ago`;
  const h = Math.floor(diffMins / 60);
  if (h < 24) return `${h} hr${h > 1 ? "s" : ""} ago`;
  return new Date(dateStr).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const mapToNotif = (item: any, readNotificationIds: string[]): Notif => {
  const isCompleted = item.status === "completed";
  const date = new Date(item.requested_at);
  const day: Notif["day"] = isToday(date) ? "today" : isYesterday(date) ? "yesterday" : "older";
  const time = isToday(date)
    ? relativeTime(item.requested_at)
    : isYesterday(date)
    ? `Yesterday, ${date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
    : date.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
  return {
    id: item._id,
    title: isCompleted ? "Consultation Completed" : "Consultation Cancelled",
    desc: `${item.patient_name || "Patient"} (${item.ticket_id || "N/A"}) • ${item.call_duration_seconds ? Math.round(item.call_duration_seconds / 60) + " min duration" : item.consultation_mode || "Video"}`,
    time,
    type: isCompleted ? "completed" : "request",
    icon: isCompleted ? "check_circle" : "cancel",
    unread: isToday(date) && !readNotificationIds.includes(item._id),
    day,
  };
};

export const useNotificationsModule = () => {
  const navigate = useNavigate();
  const {
    incomingRequest,
    countdown,
    clearIncomingRequest,
    readNotificationIds,
    markNotificationAsRead,
    markAllNotificationsAsRead,
  } = useDoctorContext();

  const [rawNotifs, setRawNotifs] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchNotificationHistory({ page: 1, limit: 20 })
      .then((res) => setRawNotifs(res.data?.data || []))
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, []);

  const notifs: Notif[] = rawNotifs.map((item) => mapToNotif(item, readNotificationIds));
  const unreadCount = notifs.filter((n) => n.unread).length + (incomingRequest ? 1 : 0);

  const handleMarkAllRead = () => {
    const unreadIds = notifs.filter((n) => n.unread).map((n) => n.id);
    markAllNotificationsAsRead(unreadIds);
  };

  const handleAccept = async () => {
    if (!incomingRequest) return;
    try {
      await respondToRequest(incomingRequest.consultation_id, "accepted");
      clearIncomingRequest();
      navigate(`/consultation/${incomingRequest.consultation_id}`);
    } catch {
      clearIncomingRequest();
    }
  };

  const handleReject = async () => {
    if (!incomingRequest) return;
    try { await respondToRequest(incomingRequest.consultation_id, "rejected"); } catch {}
    clearIncomingRequest();
  };

  const todayHistoryItems = notifs.filter((n) => n.day === "today");
  const hasUnreadToday = todayHistoryItems.some((n) => n.unread) || !!incomingRequest;

  return {
    isLoading,
    notifs,
    unreadCount,
    todayHistoryItems,
    hasUnreadToday,
    incomingRequest,
    countdown,
    handleMarkAllRead,
    handleAccept,
    handleReject,
    markNotificationAsRead,
    navigate,
  };
};
